import type { AppRouterOutputs } from '@alison/api/router';
import { Button } from '@alison/ui';
import IosShareOutlined from '@mui/icons-material/IosShareOutlined';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { TRPCClientError } from '@trpc/client';
import { type ReactNode, useState } from 'react';
import { useNotificationChannels, useSlackChannels } from '@/lib/notifications.hooks.js';
import { useShareRun } from '@/lib/workflows.hooks.js';
import { slackReasonLabel } from './automations/automationText.js';
import { EMAIL, RecipientsField, recipientsOf } from './RecipientsField.js';

const MAX_RECIPIENTS = 20;
const MAX_NOTE = 500;

const ERRORS: Record<string, string> = {
  slack_not_connected: "Slack isn't connected.",
  slack_channel_not_allowed: "You can't post to that channel.",
};

type ShareResult = AppRouterOutputs['workflows']['shareRun'];

function errorText(error: unknown): string {
  const message = error instanceof TRPCClientError ? error.message : '';
  return ERRORS[message] ?? 'The report could not be shared.';
}

function peopleText(count: number): string {
  return `${count} ${count === 1 ? 'person' : 'people'}`;
}

function slackFailureText(reason: string | null, channel: string | null): string {
  return reason === null
    ? `Could not post to #${channel}.`
    : `Could not post to #${channel}: ${slackReasonLabel(reason)}.`;
}

// "Sent to 2 people and #growth", or each leg that fell short.
export function shareResultText(
  result: ShareResult,
  channel: string | null,
): { ok: boolean; text: string } {
  const emailOk = result.sent === result.total;
  const slackOk = result.slack === null || result.slack.status === 'sent';
  const targets = [
    ...(result.sent === 0 ? [] : [peopleText(result.sent)]),
    ...(result.slack?.status === 'sent' ? [`#${channel}`] : []),
  ];
  const sentences = [
    ...(targets.length === 0 ? [] : [`Sent to ${targets.join(' and ')}.`]),
    ...(emailOk
      ? []
      : [`${result.total - result.sent} of ${result.total} emails could not be delivered.`]),
    ...(slackOk ? [] : [slackFailureText(result.slack?.reason ?? null, channel)]),
  ];
  return { ok: emailOk && slackOk, text: sentences.join(' ') };
}

export function ShareRunButton({ runId }: { runId: string }): ReactNode {
  const share = useShareRun();
  const channels = useNotificationChannels();
  const [open, setOpen] = useState(false);
  const [chips, setChips] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [note, setNote] = useState('');
  const [channelId, setChannelId] = useState('');
  const slackConnected = channels.data?.slack.status === 'connected';
  const slack = useSlackChannels(open && slackConnected);
  const recipients = recipientsOf(chips, draft);
  const invalid = recipients.filter((email) => !EMAIL.test(email));
  const channelName =
    (slack.data ?? []).find((channel) => channel.id === channelId)?.name ?? channelId;
  const canSend =
    (recipients.length > 0 || channelId !== '') &&
    recipients.length <= MAX_RECIPIENTS &&
    invalid.length === 0 &&
    !share.isPending;
  const result = share.isSuccess ? shareResultText(share.data, channelName) : null;

  const handleOpen = (): void => setOpen(true);

  const handleSend = (): void => {
    const trimmed = note.trim();
    share.mutate({
      runId,
      recipients,
      ...(channelId === '' ? {} : { slackChannelId: channelId }),
      ...(trimmed === '' ? {} : { note: trimmed }),
    });
  };

  const handleClose = (): void => {
    setOpen(false);
    // Only a settled send is cleared, so one still in flight shows its result on reopen.
    if (share.isPending) return;
    if (share.isSuccess) {
      setChips([]);
      setDraft('');
      setNote('');
      setChannelId('');
    }
    share.reset();
  };

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleOpen}
        startIcon={<IosShareOutlined sx={{ fontSize: 16 }} />}
      >
        Share
      </Button>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Share this report</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, '&&': { pt: 1.5 } }}>
          <RecipientsField
            chips={chips}
            draft={draft}
            onChange={(nextChips, nextDraft) => {
              setChips(nextChips);
              setDraft(nextDraft);
            }}
            max={MAX_RECIPIENTS}
            autoFocus
          />
          {slackConnected ? (
            <TextField
              select
              label="Slack channel (optional)"
              value={channelId}
              disabled={slack.isError}
              onChange={(event) => setChannelId(event.target.value)}
              helperText={slack.isError ? 'Slack channels are unavailable' : ' '}
            >
              <MenuItem value="">None</MenuItem>
              {(slack.data ?? []).map((channel) => (
                <MenuItem key={channel.id} value={channel.id}>
                  #{channel.name}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          <TextField
            label="Note (optional)"
            multiline
            minRows={2}
            value={note}
            onChange={(event) => setNote(event.target.value.slice(0, MAX_NOTE))}
          />
          {result === null ? null : (
            <Alert severity={result.ok ? 'success' : 'warning'}>{result.text}</Alert>
          )}
          {share.isError ? <Alert severity="error">{errorText(share.error)}</Alert> : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button variant="outlined" onClick={handleClose}>
            {share.isSuccess ? 'Done' : 'Cancel'}
          </Button>
          {share.isSuccess ? null : (
            <Button
              variant="gradient"
              disabled={!canSend}
              onClick={handleSend}
              startIcon={share.isPending ? <CircularProgress size={14} color="inherit" /> : null}
            >
              Send
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
